/** Static copy for charge recommendations, evidence holds and escalations. */
export const CLAIM_ITEM_CONTROLS_CONFIG = {
  sources: {
    MANUAL: 'Manual',
    CONFIGURED_GUIDE: 'Guide value',
    COMPANY_LIMIT: 'Company limit',
  },

  chargeDialog: {
    addTitle: 'Add Charge',
    editTitle: 'Edit Charge',
    subtitle: 'Typed charges make up the amounts requested for this repair job.',
    type: 'Type',
    currency: 'Currency',
    description: 'Description*',
    descriptionPlaceholder: 'Replacement fuel pump',
    quantity: 'Quantity',
    rate: 'Rate',
    requestedAmount: 'Requested amount*',
    taxable: 'Taxable',
    taxRate: 'Tax rate %',
    taxInclusive: 'Amounts include tax',
    cancel: 'Cancel',
    add: 'Add Charge',
    save: 'Save',
  },

  charges: {
    unitLabel: 'Unit',
    unitPlaceholder: 'Select a unit',
    allowedColumn: 'Allowed',
    adjust: 'Adjust recommendation',
    holdBadge: 'On hold',
  },

  adjust: {
    title: 'Adjust Recommendation',
    subtitle: 'Set the allowed values for this charge. Every change keeps its reason in the history.',
    requested: 'Requested',
    allowedQuantity: 'Allowed quantity',
    allowedRate: 'Allowed rate',
    allowedAmount: 'Allowed amount',
    source: 'Source*',
    sourcePlaceholder: 'Select a source',
    sourceVersion: 'Guide or limit edition',
    sourceVersionPlaceholder: 'e.g. 2026-Q3',
    reason: 'Reason*',
    reasonPlaceholder: 'Why the recommendation is changing',
    history: 'History',
    historyTitle: 'Recommendation History',
    viewHistory: 'View recommendation history',
    noHistory: 'No changes yet.',
    version: 'v',
    someone: 'Someone',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    saved: { title: 'Recommendation Updated', text: "The charge's allowed values were saved." },
    conflict: {
      title: 'Recommendation changed',
      text: "Someone else changed this charge's recommendation. Reload to see the latest before trying again.",
    },
    errorTitle: 'Something went wrong',
  },

  holds: {
    title: 'Evidence Holds',
    subtitle: 'An open hold blocks approval until the evidence is in.',
    add: 'Add Hold',
    empty: 'No holds on this job.',
    wholeJob: 'Whole job',
    removedCharge: 'Removed charge',
    due: 'Due',
    overdue: 'Overdue',
    owner: 'Owner',
    complete: 'Complete',
    cancel: 'Cancel',
    attachEvidence: 'Attach evidence',
    attached: { title: 'Evidence Attached', text: 'The files were linked to the hold.' },
    types: { INSPECTION: 'Inspection', RECORDS: 'Records' },
    statuses: { OPEN: 'Open', COMPLETED: 'Completed', CANCELLED: 'Cancelled' },
    ownerTypes: { STAFF: 'Staff', SERVICE_PROVIDER: 'Service provider' },
    completedBy: 'Completed by',
    someone: 'Someone',
    cancelledReason: 'Cancelled',
    evidence: 'Evidence',
    removedFile: '(removed)',
  },

  completeHoldDialog: {
    title: 'Complete Hold',
    noteLabel: 'Note',
    notePlaceholder: 'Optionally note what came in',
    filesLabel: 'Evidence files',
    filesPlaceholder: 'Choose files from this claim',
    noFiles: 'No files on this claim yet.',
    noOtherFiles: 'No other files on this claim.',
    alreadyAttached: 'Already attached:',
    upload: 'Upload files',
    uploading: 'Uploading…',
    uploadHint: "Saved to the claim's Attachments, visible to staff only.",
    uploadFailed: 'Upload failed',
    fileError: (fileName: string, reason: string) => `${fileName}: ${reason}`,
    wrongType: 'only PNG, JPG, GIF or PDF files can be uploaded.',
    maxFiles: (max: number) => `A hold can link up to ${max} files.`,
    back: 'Back',
    save: 'Complete',
    // Attach mode links files to an open hold without closing it.
    attach: { title: 'Attach Evidence', save: 'Attach', cancel: 'Cancel' },
  },

  holdDialog: {
    title: 'Add Evidence Hold',
    subtitle: 'The job stays in its current status; approval waits until the hold is closed.',
    type: 'Hold type*',
    charge: 'Charge',
    chargePlaceholder: 'Whole job',
    evidence: 'Requested evidence*',
    evidencePlaceholder: 'What is needed, e.g. photos of the failed part',
    owner: 'Owner*',
    ownerPlaceholder: 'Who has to act on it',
    dueDate: 'Due date*',
    openDatePicker: 'Open date picker',
    save: 'Add Hold',
    cancel: 'Cancel',
    saved: { title: 'Hold Added', text: 'The job is on hold until the evidence is in.' },
    errorTitle: 'Something went wrong',
  },

  escalations: {
    title: 'Escalations',
    add: 'Escalate',
    empty: 'No escalations.',
    raisedBy: 'Raised by',
    to: 'To',
    resolve: 'Resolve',
    resolved: 'Resolved',
    resolvedBy: 'Resolved by',
    open: 'Open',
    someone: 'Someone',
  },

  escalationDialog: {
    title: 'Escalate Job',
    subtitle: "Raise this job to someone's attention. It doesn't change the decision.",
    reason: 'Reason*',
    reasonPlaceholder: 'What needs attention',
    owner: 'Escalate to*',
    ownerPlaceholder: 'Choose a staff member',
    save: 'Escalate',
    cancel: 'Cancel',
    saved: { title: 'Job Escalated', text: 'The escalation was recorded.' },
    errorTitle: 'Something went wrong',
  },

  noteDialog: {
    cancelHold: {
      title: 'Cancel Hold',
      label: 'Reason*',
      placeholder: 'Why the hold is no longer needed',
      save: 'Cancel Hold',
      required: true,
    },
    resolveEscalation: {
      title: 'Resolve Escalation',
      label: 'Note',
      placeholder: 'Optionally note how it was resolved',
      save: 'Resolve',
      required: false,
    },
    back: 'Back',
    errorTitle: 'Something went wrong',
  },

  adjudicate: {
    openHolds: 'Approval is blocked while an evidence hold is open. Complete or cancel it on the job first.',
    aboveAllowed: 'Above the allowed amount',
  },
} as const;
